import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Row, Col, Label, FormGroup, Input } from "reactstrap";
import { Btn } from "../../../../AbstractElements";
import apiRequestHelper from "../../../../utils/apiRequestHelper";
import { toast } from "react-toastify";

interface CTAFormValues {
  heading?: string;
  paragraph?: string;
  buttonText: string;
  buttonHref: string;
  isActive: boolean;
  image: File | null;
}

const ctaValidation = Yup.object().shape({
  heading: Yup.string()
    .optional()
    .max(255, "Heading must be less than 255 characters"),
  paragraph: Yup.string()
    .optional()
    .max(1000, "Paragraph must be less than 1000 characters"),
  buttonText: Yup.string()
    .required("Button text is required")
    .max(100, "Button text must be less than 100 characters"),
  buttonHref: Yup.string()
    .required("Button link is required")
    .max(500, "URL must be less than 500 characters"),
  isActive: Yup.boolean(),
});

const CreateCTAForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams(); // expects route like /ctas/edit/:id
  const isEdit = location.pathname.includes("/edit");

  const [ctaInitialValue, setCtaInitialValue] = useState<CTAFormValues>({
    heading: "",
    paragraph: "",
    buttonText: "",
    buttonHref: "",
    isActive: true,
    image: null,
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Fetch CTA if edit
  useEffect(() => {
    const fetchCTA = async () => {
      if (!isEdit || !id) return;

      try {
        setLoading(true);
        const res = await apiRequestHelper.get(`/ctas/${id}`);

        // Adjust mapping if your API shape differs:
        const cta = res.data?.data?.cta || res.data?.data;

        setCtaInitialValue({
          heading: cta?.heading ?? "",
          paragraph: cta?.paragraph ?? "",
          buttonText: cta?.buttonText ?? "",
          buttonHref: cta?.buttonHref ?? "",
          isActive: typeof cta?.isActive === "boolean" ? cta.isActive : true,
          image: null, // keep file empty; user can upload new one if needed
        });

        // If API returns imageUrl, show it as preview
        setImagePreview(cta?.imageUrl ?? null);
      } catch (error: any) {
        toast.error(
          error.response?.data?.messages?.[0]?.message ||
            error.response?.data?.message ||
            "Failed to load CTA"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCTA();
  }, [isEdit, id]);

  const handleImageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: any
  ) => {
    const file = event.currentTarget.files?.[0];
    if (file) {
      setFieldValue("image", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setFieldValue("image", null);
      // If user clears file input on edit, keep existing preview OR clear?
      // Keeping same as your original behavior: clear preview
      setImagePreview(null);
    }
  };

  const handleSubmit = async (values: CTAFormValues) => {
    try {
      setLoading(true);

      const formData = new FormData();
      if (values.heading) formData.append("heading", values.heading);
      if (values.paragraph) formData.append("paragraph", values.paragraph);
      formData.append("buttonText", values.buttonText);
      formData.append("buttonHref", values.buttonHref);
      formData.append("isActive", values.isActive.toString());
      if (values.image) formData.append("image", values.image);

      if (isEdit && id) {
        await apiRequestHelper.put(`/ctas/${id}`, formData, true);
        toast.success("CTA updated successfully!");
      } else {
        await apiRequestHelper.post("/ctas", formData, true);
        toast.success("CTA created successfully!");
      }

      navigate("/ctas/cards");
    } catch (error: any) {
      console.error("Error saving CTA:", error);
      toast.error(
        error.response?.data?.messages?.[0]?.message ||
          error.response?.data?.message ||
          "Request failed!"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={ctaInitialValue}
      validationSchema={ctaValidation}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        setFieldValue,
      }) => (
        <Form className="theme-form">
          <Row>
            <Col md="6">
              <FormGroup>
                <Label>Heading</Label>
                <input
                  className="form-control"
                  type="text"
                  name="heading"
                  value={values.heading}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter CTA heading"
                  disabled={loading}
                />
                {errors.heading && touched.heading && (
                  <div className="text-danger mt-1">{errors.heading}</div>
                )}
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label>
                  Button Text <span className="txt-danger">*</span>
                </Label>
                <input
                  className="form-control"
                  type="text"
                  name="buttonText"
                  value={values.buttonText}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter button text"
                  disabled={loading}
                />
                {errors.buttonText && touched.buttonText && (
                  <div className="text-danger mt-1">{errors.buttonText}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="12">
              <FormGroup>
                <Label>Paragraph</Label>
                <textarea
                  className="form-control"
                  name="paragraph"
                  value={values.paragraph}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="Enter CTA description"
                  rows={4}
                  disabled={loading}
                />
                {errors.paragraph && touched.paragraph && (
                  <div className="text-danger mt-1">{errors.paragraph}</div>
                )}
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label>
                  Button Link (URL) <span className="txt-danger">*</span>
                </Label>
                <input
                  className="form-control"
                  type="text"
                  name="buttonHref"
                  value={values.buttonHref}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="https://example.com"
                  disabled={loading}
                />
                {errors.buttonHref && touched.buttonHref && (
                  <div className="text-danger mt-1">{errors.buttonHref}</div>
                )}
              </FormGroup>
            </Col>

            <Col md="6">
              <FormGroup>
                <Label>CTA Image</Label>
                <input
                  className="form-control"
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageChange(e, setFieldValue)}
                  disabled={loading}
                />
              </FormGroup>
            </Col>
          </Row>

          {imagePreview && (
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Image Preview</Label>
                  <div>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        maxWidth: "300px",
                        maxHeight: "200px",
                        borderRadius: "8px",
                      }}
                    />
                  </div>
                </FormGroup>
              </Col>
            </Row>
          )}

          <Row>
            <Col md="12">
              <FormGroup check>
                <Input
                  type="checkbox"
                  name="isActive"
                  checked={values.isActive}
                  onChange={handleChange}
                  disabled={loading}
                />
                <Label check>Active (Show this CTA to users)</Label>
              </FormGroup>
            </Col>
          </Row>

          <Row className="mt-3">
            <Col>
              <div className="text-end">
                <Btn
                  color="secondary"
                  className="me-2"
                  onClick={() => navigate("/ctas/cards")}
                  type="button"
                  disabled={loading}
                >
                  Cancel
                </Btn>

                <Btn
                  className="btn-edit-custom "
                  type="submit"
                  disabled={loading}
                >
                  {loading
                    ? "Please wait..."
                    : isEdit
                    ? "Update CTA"
                    : "Create CTA"}
                </Btn>
              </div>
            </Col>
          </Row>
        </Form>
      )}
    </Formik>
  );
};

export default CreateCTAForm;
